import {
  FBN_LOOKBACK_DAYS,
  FBN_MAX_DETAILS,
  FBN_MAX_ROWS,
  FBN_ORIGIN,
  FBN_SEARCH_URL,
  HDL_BROWSER_HOSTS,
  OCHCA_PLAN_REVIEW_URL,
  OCHCA_PORTAL_ORIGIN,
  PLAN_CHECK_FIELDS,
} from '../../../shared/fields';
import type { FbnDetailRecord, FbnRowRecord, PlanCheckRecord } from '../../../shared/fields';

/** The "Send to CRM" bookmark (D58, D62, D76). OC Health's portal and nine cities' HdL license searches
 *  block automated clients, and the county's server never answers Cloudflare's network, so the rep's own
 *  browser does the reading. On the plan-review page, one click asks the portal for its food plan checks
 *  of the last 60 days (newest first, 100 per page, usually one request — the same call the page makes);
 *  on a city's HdL site, for the licenses started in the last 45 days (the search the page's form makes),
 *  read from its results table; on the county's filings search, the last seven filing days' searches
 *  (the page's own requests), then — once this app says which filings are worth opening — their pages.
 *  Each hands its result to this app's import screen with postMessage, only to this app's origin. No key
 *  and no data travel in a URL. */

export const PORTAL_ORIGIN = OCHCA_PORTAL_ORIGIN;
export const READY_MESSAGE = 'oc-crm:ready-for-plan-checks';
export const PLAN_CHECKS_MESSAGE = 'oc-crm:plan-checks';
export const CITY_LICENSES_MESSAGE = 'oc-crm:city-licenses';
/** The county's hand-off (D76): the rows of the days searched; the files this app wants opened; their details. */
export const FBN_ROWS_MESSAGE = 'oc-crm:fbn-rows';
export const FBN_WANTED_MESSAGE = 'oc-crm:fbn-wanted';
export const FBN_DETAILS_MESSAGE = 'oc-crm:fbn-details';
/** The HdL cities' origins the bookmark runs on, and the import screen hears (D62). */
export const HDL_ORIGINS: readonly string[] = Object.values(HDL_BROWSER_HOSTS);
const HDL_DAYS = 45;
const MAX_ROWS = 3000;
const MAX_CELLS = 20;
const MAX_CELL = 300;
const LOOKBACK_DAYS = 60;
const MAX_RECORDS = 500;

export const planCheckBookmarklet = (appOrigin: string): string => {
  const s = JSON.stringify;
  const code = `void (async () => {
  const APP = ${s(appOrigin)}, PORTAL = ${s(PORTAL_ORIGIN)}, FIELDS = ${s(PLAN_CHECK_FIELDS)}, HDL = ${s(HDL_ORIGINS)};
  const COUNTY = ${s(FBN_ORIGIN)}, SEARCH = ${s(FBN_SEARCH_URL)};
  const onHdl = HDL.includes(location.origin), onCounty = location.origin === COUNTY;
  if (location.origin !== PORTAL && !onHdl && !onCounty) {
    if (confirm('Open the OC Health plan-review page? Then click Send to CRM again.')) location.href = ${s(OCHCA_PLAN_REVIEW_URL)};
    return;
  }
  const w = window.open(APP + '/#/import/plan-checks', '_blank');
  if (!w) { alert('Allow pop-ups for this page, then click Send to CRM again.'); return; }
  let message, onWanted = null;
  if (onCounty) {
    try {
      const hdr = { 'content-type': 'application/x-www-form-urlencoded; charset=UTF-8', 'x-requested-with': 'XMLHttpRequest' };
      const ask = async (p, body) => {
        const r = await fetch(new URL('Presentors/' + p, SEARCH).href, { method: 'POST', headers: hdr, body });
        if (!r.ok) throw new Error('the county answered ' + r.status + '. Reload the page and try again.');
        const t = await r.text();
        if (/session has expired/i.test(t)) throw new Error('the session expired. Reload the page and try again.');
        if (/^Error in /.test(t)) throw new Error('the county answered: ' + t.slice(0, 80));
        return t;
      };
      const doc = (t) => new DOMParser().parseFromString(t, 'text/html');
      const results = (t) => {
        const d = doc(t);
        if (!d.querySelector('#SearchResultsTitle1_resultCount')) throw new Error('the county answered no results page. Open the business-name search and try again.');
        return d;
      };
      const text = (el) => ((el && el.textContent) || '').trim();
      const two = (n) => String(n).padStart(2, '0');
      const iso = (d) => d.getFullYear() + '-' + two(d.getMonth() + 1) + '-' + two(d.getDate());
      const us = (d) => (d.getMonth() + 1) + '/' + d.getDate() + '/' + d.getFullYear();
      const toIso = (t) => { const m = t.match(/^(\\d{1,2})\\/(\\d{1,2})\\/(\\d{4})$/); return m ? m[3] + '-' + two(m[1]) + '-' + two(m[2]) : ''; };
      const rowsOf = (t) => [...results(t).querySelectorAll('tr.searchResultRow')].flatMap((tr) => {
        const link = tr.querySelector('#EntityTitleDocNum_docLinkTD');
        const m = ((link && link.getAttribute('onclick')) || '').match(/showDetails\\(event, '([^']*)'/);
        const file = text(tr.querySelector('#EntityTitleDocNum_docNumber')), name = text(tr.querySelector('#businessName')), filedOn = toIso(text(tr.querySelector('#dataFiled')));
        return file && name && filedOn && m ? [{ file, name, filedOn, detail: m[1] }] : [];
      });
      const days = [], rows = [], byFile = {};
      for (let n = 1; n <= ${FBN_LOOKBACK_DAYS}; n++) {
        const d = new Date(); d.setDate(d.getDate() - n); days.push(iso(d));
        const q = '&FromDate=' + us(d) + '&ToDate=' + us(d) + '&ERetrievalGroup=&SearchMode=3';
        const first = q + '&IsNewSearch=true&searchActionType=';
        await ask('SearchHelpPresentor.aspx', first);
        const t1 = await ask('AjaxPresentor.aspx', first);
        const pages = Math.min(Math.ceil(Number(text(results(t1).querySelector('#SearchResultsTitle1_resultCount')) || 0) / 20), 15);
        const got = rowsOf(t1);
        for (let p = 2; p <= pages; p++) got.push(...rowsOf(await ask('AjaxPresentor.aspx', 'PageNum=' + p + q + '&searchActionType=')));
        for (const r of got) if (rows.length < ${FBN_MAX_ROWS}) { rows.push(r); byFile[r.file] = r; }
      }
      message = { type: ${s(FBN_ROWS_MESSAGE)}, days, rows };
      onWanted = async (files) => {
        const details = [];
        let error = null;
        try {
        for (const f of files.slice(0, ${FBN_MAX_DETAILS})) {
          const r = byFile[f];
          if (!r) continue;
          const html = await ask('DetailsPresentor.aspx', r.detail);
          const file = ((html.match(/id="DocumentSpinner1_docNumber"[^>]*>\\s*(\\d+)/) || [])[1]) || '';
          const plain = html.replace(/<[^>]+>/g, ' ').replace(/&#8211;|&ndash;/g, '\\u2013').replace(/&#39;/g, "'").replace(/&quot;/g, '"').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/\\s+/g, ' ');
          const registrants = (((plain.match(/Registrants(.*?)Status/) || [])[1]) || '').split(/\\bName\\b/).slice(1).map((x) => x.replace(/\\b\\d+ of \\d+\\b/g, '').trim()).filter(Boolean);
          const statements = [...new Set([...plain.matchAll(/FBN STATEMENT \\d{4} [\\u2013-] (\\d{8,})/g)].map((m) => m[1]))];
          if (file) details.push({ file, registrants, statements });
        }
        } catch (e) { error = e.message; }
        w.postMessage(error ? { type: ${s(FBN_DETAILS_MESSAGE)}, details, error } : { type: ${s(FBN_DETAILS_MESSAGE)}, details }, APP);
      };
    } catch (e) { alert('Could not read the county filings: ' + e.message); w.close(); return; }
  }
  if (onHdl) {
    try {
      let page = document;
      if (!page.querySelector('[name=WebInstanceId]'))
        page = new DOMParser().parseFromString(await (await fetch('/Search/Index/BusinessLicense')).text(), 'text/html');
      const instance = page.querySelector('[name=WebInstanceId]');
      if (!instance) throw new Error('this site has no license search.');
      const us = (n) => { const d = new Date(Date.now() - n * 864e5); return String(d.getMonth() + 1).padStart(2, '0') + '/' + String(d.getDate()).padStart(2, '0') + '/' + d.getFullYear(); };
      const profile = page.querySelector('[name=WebInstanceProfileName]');
      const body = new URLSearchParams({ SelectedSearchType: 'Business Name', SearchString: '', FromDate: us(${HDL_DAYS}), ToDate: us(0),
        WebInstanceId: instance.value, WebInstanceProfileName: profile ? profile.value : 'BusinessLicense', submitButton: 'Search' });
      const r = await fetch('/Search/SearchBy', { method: 'POST', body });
      if (!r.ok) throw new Error('the city answered ' + r.status + '. Reload the page and try again.');
      const found = new DOMParser().parseFromString(await r.text(), 'text/html');
      const tables = [...found.querySelectorAll('table')].map((t) => [...t.querySelectorAll('tr')].map((tr) => [...tr.querySelectorAll('th,td')].map((c) => c.textContent.replace(/\\s+/g, ' ').trim())));
      const rows = tables.find((t) => t.length > 0 && t[0].includes('Business Name'));
      if (!rows) throw new Error('no results table on the page.');
      message = { type: ${s(CITY_LICENSES_MESSAGE)}, city: location.origin, rows: rows.slice(0, ${MAX_ROWS}) };
    } catch (e) { alert('Could not read the licenses: ' + e.message); w.close(); return; }
  }
  if (!message) {
  const since = new Date(Date.now() - ${LOOKBACK_DAYS} * 864e5).toISOString().slice(0, 10);
  const day = (x) => String(x && x.PlanReviewSubmissionDate).slice(0, 10);
  const records = [];
  try {
    for (let page = 1; page <= 5; page++) {
      const r = await fetch('/orange-county/plan-reviews', { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jurisdiction: 'orange-county', userAction: 'getAllPlanReviews', searchStr: 'Food', page, rows: 100 }) });
      const body = r.ok ? await r.json() : null;
      const rows = body && body.data && body.data.DATA;
      if (!r.ok || !Array.isArray(rows)) throw new Error('OC Health answered ' + r.status + '. Reload the page and try again.');
      for (const x of rows) {
        if (day(x) < since) continue;
        const o = {};
        for (const f of FIELDS) o[f] = x[f] == null ? null : String(x[f]);
        records.push(o);
      }
      if (rows.length < 100 || day(rows[rows.length - 1]) < since) break;
    }
  } catch (e) { alert('Could not read the plan checks: ' + e.message); w.close(); return; }
  message = { type: ${s(PLAN_CHECKS_MESSAGE)}, records };
  }
  const send = (e) => {
    if (e.origin !== APP || e.source !== w || e.data !== ${s(READY_MESSAGE)}) return;
    w.postMessage(message, APP);
    removeEventListener('message', send);
  };
  addEventListener('message', send);
  if (onWanted) {
    const wanted = (e) => {
      if (e.origin !== APP || e.source !== w || !e.data || e.data.type !== ${s(FBN_WANTED_MESSAGE)} || !Array.isArray(e.data.files)) return;
      removeEventListener('message', wanted);
      onWanted(e.data.files).catch((err) => alert('Could not open the filings: ' + err.message));
    };
    addEventListener('message', wanted);
  }
})();`;
  return `javascript:${encodeURIComponent(code)}`;
};

/** What the import screen accepts from the portal window: the agreed fields only, as text. */
export const cleanPlanChecks = (raw: readonly unknown[]): PlanCheckRecord[] =>
  raw
    .filter((x): x is Record<string, unknown> => typeof x === 'object' && x !== null)
    .slice(0, MAX_RECORDS)
    .map((x) => {
      const out: PlanCheckRecord = {};
      for (const f of PLAN_CHECK_FIELDS) {
        const v = x[f];
        if (typeof v === 'string' || typeof v === 'number') out[f] = String(v).slice(0, 2000);
      }
      return out;
    });

/** The source an HdL page's origin stands for, or null for any other origin. */
export const hdlSourceOf = (origin: string): keyof typeof HDL_BROWSER_HOSTS | null =>
  (Object.entries(HDL_BROWSER_HOSTS).find(
    ([, host]) => host === origin,
  )?.[0] as keyof typeof HDL_BROWSER_HOSTS) ?? null;

/** What the import screen accepts from a city's page: rows of text cells, within limits. */
export const cleanCityRows = (raw: readonly unknown[]): string[][] =>
  raw
    .filter((row): row is unknown[] => Array.isArray(row))
    .slice(0, MAX_ROWS)
    .map((row) =>
      row
        .slice(0, MAX_CELLS)
        .map((cell) =>
          typeof cell === 'string' || typeof cell === 'number' ? String(cell).slice(0, MAX_CELL) : '',
        ),
    );

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const str = (v: unknown, max: number): string =>
  typeof v === 'string' || typeof v === 'number' ? String(v).slice(0, max) : '';
const strs = (v: unknown, count: number, max: number): string[] =>
  Array.isArray(v)
    ? v
        .slice(0, count)
        .map((x) => str(x, max))
        .filter(Boolean)
    : [];
const objects = (raw: readonly unknown[]): Record<string, unknown>[] =>
  raw.filter((x): x is Record<string, unknown> => typeof x === 'object' && x !== null);

/** What the import screen accepts from the county page (D76): the days searched, the rows and the details,
 *  as text, within limits. */
export const cleanFbnDays = (raw: readonly unknown[]): string[] =>
  raw.filter((d): d is string => typeof d === 'string' && DAY.test(d)).slice(0, 31);
export const cleanFbnRows = (raw: readonly unknown[]): FbnRowRecord[] =>
  objects(raw)
    .slice(0, FBN_MAX_ROWS)
    .flatMap((x) => {
      const r = {
        file: str(x.file, 40),
        name: str(x.name, 300),
        filedOn: str(x.filedOn, 10),
        detail: str(x.detail, 2000),
      };
      return r.file && DAY.test(r.filedOn) ? [r] : [];
    });
export const cleanFbnDetails = (raw: readonly unknown[]): FbnDetailRecord[] =>
  objects(raw)
    .slice(0, FBN_MAX_DETAILS)
    .flatMap((x) => {
      const file = str(x.file, 40);
      return file
        ? [{ file, registrants: strs(x.registrants, 20, 300), statements: strs(x.statements, 50, 40) }]
        : [];
    });

export type AcceptedMessage =
  | { kind: 'plan-checks'; records: PlanCheckRecord[] }
  | { kind: 'city'; source: keyof typeof HDL_BROWSER_HOSTS; rows: string[][] }
  | { kind: 'fbn-rows'; days: string[]; rows: FbnRowRecord[] }
  | { kind: 'fbn-details'; details: FbnDetailRecord[]; error?: string };

/** What the import screen takes from a message: only from the window that opened it, plan checks only
 *  from OC Health's portal, a city's table only from that city's own site, and the county's rows and
 *  details only from the county's. Anything else is null. */
export const acceptMessage = (e: MessageEvent, opener: Window | null): AcceptedMessage | null => {
  if (!opener || e.source !== opener) return null;
  const data = e.data as {
    type?: unknown;
    records?: unknown;
    rows?: unknown;
    city?: unknown;
    days?: unknown;
    details?: unknown;
    error?: unknown;
  } | null;
  if (e.origin === PORTAL_ORIGIN && data?.type === PLAN_CHECKS_MESSAGE && Array.isArray(data.records))
    return { kind: 'plan-checks', records: cleanPlanChecks(data.records) };
  if (
    e.origin === FBN_ORIGIN &&
    data?.type === FBN_ROWS_MESSAGE &&
    Array.isArray(data.rows) &&
    Array.isArray(data.days)
  )
    return { kind: 'fbn-rows', days: cleanFbnDays(data.days), rows: cleanFbnRows(data.rows) };
  if (e.origin === FBN_ORIGIN && data?.type === FBN_DETAILS_MESSAGE && Array.isArray(data.details)) {
    const error = str(data.error, 300);
    return { kind: 'fbn-details', details: cleanFbnDetails(data.details), ...(error ? { error } : {}) };
  }
  const source = hdlSourceOf(e.origin);
  if (source && data?.type === CITY_LICENSES_MESSAGE && data.city === e.origin && Array.isArray(data.rows))
    return { kind: 'city', source, rows: cleanCityRows(data.rows) };
  return null;
};
